import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";

const AuthCtx = createContext(null);

// Sesión en caché (token + usuario guardados en el login): permite pintar la app al instante en vez de
// esperar a /auth/me. El backend sigue validando cada petición; /auth/me confirma en segundo plano.
const readCachedUser = () => {
  try {
    if (!localStorage.getItem("sns_token")) return null;
    const raw = localStorage.getItem("sns_user");
    const u = raw ? JSON.parse(raw) : null;
    return u && u.id ? u : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readCachedUser); // null = checking, false = not logged, obj = logged
  const [loading, setLoading] = useState(user === null); // con sesión en caché no hay pantalla de carga

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("sns_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      // Mantener la misma referencia si no ha cambiado nada: varias pantallas dependen de `user`
      setUser((prev) => (prev && JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
      localStorage.setItem("sns_user", JSON.stringify(data));
      // Sesión existente al abrir la app → intento silencioso de re-registrar token
      import("@/lib/push").then((m) => m.registerPushIfPossible().catch(() => {}));
    } catch {
      localStorage.removeItem("sns_token");
      localStorage.removeItem("sns_user");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Listener global de push en primer plano: muestra toast y refresca el badge de pendientes
  useEffect(() => {
    const onPush = (ev) => {
      const { title, body, native_shown } = ev.detail || {};
      // Si el navegador YA mostró la notificación del sistema, evitamos duplicar con el toast
      if (!native_shown && (title || body)) {
        toast(title || "Notificación", { description: body || "" });
      }
      // Siempre pedimos refresco del badge (nuevo trabajo asignado, etc.)
      window.dispatchEvent(new CustomEvent("sns:refresh-pending"));
    };
    window.addEventListener("sns:push", onPush);
    return () => window.removeEventListener("sns:push", onPush);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (!data || !data.token || !data.user) {
      throw new Error("Respuesta inválida del servidor de autenticación");
    }
    localStorage.setItem("sns_token", data.token);
    localStorage.setItem("sns_user", JSON.stringify(data.user));
    setUser(data.user);
    // Registrar token de push silenciosamente (solo si ya tiene permiso concedido)
    import("@/lib/push").then((m) => m.registerPushIfPossible().catch(() => {}));
    return data.user;
  };

  const logout = () => {
    // Desregistrar token del backend (mejor esfuerzo)
    import("@/lib/push").then((m) => m.unregisterCurrentToken().catch(() => {}));
    localStorage.removeItem("sns_token");
    localStorage.removeItem("sns_user");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, logout, refresh: fetchMe, fmtErr }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
